/**
 * AdBit Tracking Utility
 * Handles Meta Pixel events with consent checking and event deduplication.
 * Events only fire if user has given advertising/analytics consent.
 */

import { getCookieConsent } from "@/components/CookieBanner";

const META_PIXEL_ID = "1316242720526518";

// Generate unique event ID for deduplication between Pixel and CAPI
export function generateEventId() {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

// ── Meta Pixel Events ──

export function trackPageView() {
  const consent = getCookieConsent();
  if (!consent?.advertising || !window.fbq) return;
  window.fbq("track", "PageView");
}

export function trackSignUp(email) {
  const consent = getCookieConsent();
  const eventId = generateEventId();
  if (consent?.advertising && window.fbq) {
    window.fbq("track", "CompleteRegistration", { content_name: "signup" }, { eventID: eventId });
  }
  // Send server-side event
  _sendServerEvent("CompleteRegistration", eventId, { email });
}

export function trackStartTrial(email) {
  const consent = getCookieConsent();
  const eventId = generateEventId();
  if (consent?.advertising && window.fbq) {
    window.fbq("track", "StartTrial", { content_name: "trial" }, { eventID: eventId });
  }
  _sendServerEvent("StartTrial", eventId, { email });
}

export function trackAnalysisRun(analysisId, objective) {
  const consent = getCookieConsent();
  const eventId = generateEventId();
  if (consent?.advertising && window.fbq) {
    window.fbq("track", "ViewContent", {
      content_name: "analysis_run",
      content_category: objective,
      content_ids: [analysisId],
    }, { eventID: eventId });
  }
  _sendServerEvent("ViewContent", eventId, { content_name: "analysis_run", objective });
}

export function trackPurchase(planName, value, currency = "USD") {
  const consent = getCookieConsent();
  const eventId = generateEventId();
  if (consent?.advertising && window.fbq) {
    window.fbq("track", "Purchase", {
      content_name: planName,
      value: value,
      currency: currency,
    }, { eventID: eventId });
  }
  _sendServerEvent("Purchase", eventId, { plan: planName, value, currency });
}

export function trackInitiateCheckout(planName, value, currency = "USD") {
  const consent = getCookieConsent();
  const eventId = generateEventId();
  if (consent?.advertising && window.fbq) {
    window.fbq("track", "InitiateCheckout", {
      content_name: planName,
      value: value,
      currency: currency,
    }, { eventID: eventId });
  }
  _sendServerEvent("InitiateCheckout", eventId, { plan: planName, value, currency });
}

export function trackLead(source) {
  const consent = getCookieConsent();
  const eventId = generateEventId();
  if (consent?.advertising && window.fbq) {
    window.fbq("track", "Lead", { content_name: source }, { eventID: eventId });
  }
  _sendServerEvent("Lead", eventId, { source });
}

// ── GA4 Events ──

export function ga4Event(eventName, params = {}) {
  const consent = getCookieConsent();
  if (!consent?.analytics || !window.gtag) return;
  window.gtag("event", eventName, params);
}

// ── Server-Side Event (for Meta CAPI deduplication) ──

const API = process.env.REACT_APP_BACKEND_URL;

async function _sendServerEvent(eventName, eventId, data = {}) {
  const consent = getCookieConsent();
  if (!consent?.advertising) return;
  try {
    // Get fbp and fbc cookies for better matching
    const fbp = document.cookie.split(";").find(c => c.trim().startsWith("_fbp="))?.split("=")[1] || "";
    const fbc = document.cookie.split(";").find(c => c.trim().startsWith("_fbc="))?.split("=")[1] || "";
    await fetch(`${API}/api/track/meta`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: window.location.href,
        fbp, fbc,
        ...data,
      }),
    });
  } catch (err) {
    // Fail silently — tracking should never block the user
    console.debug("CAPI event failed:", err);
  }
}
